import type { FunctionComponent } from 'react';

import { plural } from '../../../../../../shared/helpers/plural';
import { Action } from '../../../../../../typings/permissions';
import { userHasPermissionForTeam } from '../../../../../helpers/permissions';
import useGetTeamsForProjectOrReleaseGroup from '../../../../../hooks/api/teams/useGetTeamsForProjectOrReleaseGroup';
import Icon from '../../../../generic/Icon/Icon';
import { icons } from '../../../../generic/Icon/icons';
import RouterLink from '../../../../generic/Link/RouterLink';
import Loader from '../../../../generic/Loader';
import Tooltip from '../../../../generic/Tooltip';
import type { TeamForProjectOrReleaseGroup } from '../../../../../hooks/api/teams/useGetTeamsForProjectOrReleaseGroup';

import { InfoCard } from './InfoCard';

import styles from './TeamAccessCard.module.less';

const TEAM_LINK_BASE = '/account/settings/organization/teams/';
const getTeamURL = (id: number): string => `${TEAM_LINK_BASE}${id}`;

const MAX_TEAMS_DISPLAYED = 3;

type TeamRowProps = {
  team: TeamForProjectOrReleaseGroup;
};

const TeamRow: FunctionComponent<TeamRowProps> = ({ team }) => {
  const canEdit = userHasPermissionForTeam(Action.Edit, team.id);
  const content = canEdit ? (
    <RouterLink to={getTeamURL(team.id)} className={styles.teamName}>
      {team.name}
    </RouterLink>
  ) : (
    <span className={styles.teamName}>{team.name}</span>
  );

  return (
    <div className={styles.team}>
      {content}
      <span className={styles.chevron}>
        <Icon icon={icons.chevronRight} />
      </span>
    </div>
  );
};

type TeamAccessMoreProps = {
  hiddenTeams: TeamForProjectOrReleaseGroup[];
};

const TeamAccessMore: FunctionComponent<TeamAccessMoreProps> = ({ hiddenTeams }) => (
  <Tooltip
    id="team-access-more"
    tooltip={hiddenTeams.map((team) => <div key={team.id}>{team.name}</div>)}
  >
    <span className={styles.more}>+ {hiddenTeams.length} More</span>
  </Tooltip>
);

const getHiddenTeamsText = (hiddenCount: number, isProject: boolean): string => {
  const teamsPluralized = plural(hiddenCount, 'team');
  const hasPluralized = hiddenCount === 1 ? 'has' : 'have';
  const noun = isProject ? 'project' : 'release group';
  return `and ${hiddenCount} other ${teamsPluralized} ${hasPluralized} access to this ${noun}.`;
};

const getEmptyText = (isProject: boolean): string => (
  isProject ? 'No teams affiliated with this project.' : 'No teams affiliated with this release group.'
);

type Props = {
  projectLocator?: string;
  releaseGroupId?: number;
  isLoading: boolean;
};

/**
 * Card on the project (or release group) summary page that shows the teams with access to it.
 * Pass exactly one of `projectLocator` or `releaseGroupId`.
 */
export const TeamAccessCard: FunctionComponent<Props> = ({ projectLocator, releaseGroupId, isLoading }) => {
  const { data, isLoading: isFetchingTeams } = useGetTeamsForProjectOrReleaseGroup(projectLocator, releaseGroupId);
  const teams = data?.teams ?? [];
  const totalCount = data?.totalCount ?? 0;
  const hiddenCount = totalCount - teams.length;
  const isPending = isLoading || isFetchingTeams;
  const isProject = !!projectLocator;

  if (isPending) {
    return (
      <InfoCard title="Team Access" icon={icons.users}>
        <Loader />
      </InfoCard>
    );
  }

  if (totalCount === 0) {
    return (
      <InfoCard title="Team Access" icon={icons.users} count={0}>
        <span className={styles.empty}>{getEmptyText(isProject)}</span>
      </InfoCard>
    );
  }

  //  When the user can see every team, truncate to MAX and show a "+ N More" tooltip with the rest.
  if (hiddenCount === 0) {
    const visibleTeams = teams.slice(0, MAX_TEAMS_DISPLAYED);
    const overflowTeams = teams.slice(MAX_TEAMS_DISPLAYED);
    return (
      <InfoCard title="Team Access" icon={icons.users} count={totalCount}>
        {visibleTeams.map((team) => <TeamRow key={team.id} team={team} />)}
        {overflowTeams.length > 0 && <TeamAccessMore hiddenTeams={overflowTeams} />}
      </InfoCard>
    );
  }

  //  Permission-restricted: list every visible team and call out the rest in copy.
  return (
    <InfoCard title="Team Access" icon={icons.users} count={totalCount}>
      {teams.map((team) => <TeamRow key={team.id} team={team} />)}
      <span className={styles.hidden}>{getHiddenTeamsText(hiddenCount, isProject)}</span>
    </InfoCard>
  );
};
